import React from 'react';
import { CardElement, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      '::placeholder': { color: '#a78bfa' },
    },
    invalid: { color: '#ef4444' },
  },
};

export const PaymentSection = ({
  paymentMethod,
  setPaymentMethod,
  paymentRequest,
  pricing,
  stripe,
  loading,
  onStripeSubmit,
  createPayPalOrder,
  onPayPalApprove,
}) => (
  <Card className="bg-purple-800/50 border-purple-600">
    <CardContent className="pt-6">
      <h2 className="text-2xl font-bold text-white mb-6">Payment Method</h2>

      {/* Express Checkout - only renders when Stripe confirms a wallet is available */}
      {paymentRequest && (
        <div className="mb-6">
          <p className="text-purple-200 text-sm mb-3">Express Checkout</p>
          <div className="bg-white rounded-lg p-1 mb-4">
            <PaymentRequestButtonElement
              options={{
                paymentRequest,
                style: {
                  paymentRequestButton: {
                    type: 'buy',
                    theme: 'dark',
                    height: '48px',
                  },
                },
              }}
            />
          </div>
          <p className="text-purple-400 text-xs text-center mb-4">
            One-tap pay with Apple Pay, Google Pay, or your saved Link card on supported devices
          </p>
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-purple-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-purple-800 text-purple-300">Or pay with</span>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-4 mb-6">
        <button
          type="button"
          onClick={() => setPaymentMethod('stripe')}
          className={`flex-1 p-4 rounded-lg border-2 transition-all ${
            paymentMethod === 'stripe'
              ? 'border-yellow-400 bg-yellow-500/10'
              : 'border-purple-600 bg-purple-800/30'
          }`}
          data-testid="payment-method-stripe"
        >
          <p className="text-white font-semibold">Credit Card</p>
          <p className="text-purple-300 text-sm">Visa, Mastercard, Amex</p>
        </button>
        <button
          type="button"
          onClick={() => setPaymentMethod('paypal')}
          className={`flex-1 p-4 rounded-lg border-2 transition-all ${
            paymentMethod === 'paypal'
              ? 'border-yellow-400 bg-yellow-500/10'
              : 'border-purple-600 bg-purple-800/30'
          }`}
          data-testid="payment-method-paypal"
        >
          <p className="text-white font-semibold">PayPal</p>
          <p className="text-purple-300 text-sm">Fast & Secure</p>
        </button>
      </div>

      {paymentMethod === 'stripe' && (
        <form onSubmit={onStripeSubmit}>
          <div className="bg-purple-900/50 p-4 rounded border border-purple-600 mb-4">
            <CardElement options={CARD_ELEMENT_OPTIONS} />
          </div>
          <Button
            type="submit"
            disabled={!stripe || loading}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-purple-900 font-semibold py-6 text-lg"
            data-testid="checkout-pay-btn"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              `Pay $${pricing?.total?.toFixed(2) || '0.00'}`
            )}
          </Button>
        </form>
      )}

      {paymentMethod === 'paypal' && pricing && (
        <div className="mt-4">
          <PayPalButtons
            createOrder={createPayPalOrder}
            onApprove={onPayPalApprove}
            onError={(err) => {
              console.error('PayPal error:', err);
              toast.error('PayPal payment failed');
            }}
            style={{ layout: 'vertical' }}
          />
        </div>
      )}
    </CardContent>
  </Card>
);
